import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../hooks/useAppDispatch";
import { useAppSelector } from "../../../../hooks/useAppSelector";
import {
  addSelected,
  deleteAllSelected,
  deleteSelected,
  fetchKrookies,
  setData,
} from "../../../../services/reducers/table";
import RowMenu, {
  MenuItem,
} from "../../../ui-kit/data-table/row-menu/row-menu";
import {
  fetchCoordinatesData,
  fetchKrookieData,
  setResetKrookieData,
} from "../../../../services/reducers/master-plan-modification";
import TableContainer from "../../../ui-kit/data-table/table/table-container";
import TableColumn from "../../../ui-kit/data-table/table-column/table-column";
import DocumentKrookie from "../../../../shared/document-krookie/document-krookie";
import ModalSuccessfulApproveKrookie from "../../master-plan-modification-portal/modals/modal-successful-approve-krookie/modal-successful-approve-krookie";
import ModalRejectKrookie from "../../master-plan-modification-portal/modals/modal-reject-krookie/modal-reject-krookie";
import Modal from "../../../../shared/modal/modal";
import CoordinatesBlock from "../../../../shared/coordinates-block/coordinates-block";
import styles from "./krookie-journal.module.scss";
import ExportButtons from "./export-buttons/export-buttons";
import { DocumentOrigin } from "../../../../interfaces/krookie";
import krookieJournalIcon from "../../../../images/krookie-journal.svg";
import { renderStatusTag } from "../../../../utils/render-tags";

const KrookieJournal = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { data, totalDataSize, selected, fullData, isLoading } = useAppSelector(
    (store) => store.table,
  );
  const { convertedKrookieData, coordinatesData } = useAppSelector(
    (state) => state.masterPlanModification,
  );

  const { layers, arcgisTokens } = useAppSelector((state) => state.map);

  const [visibleDocumentKrookie, setVisibleDocumentKrookie] =
    useState<boolean>(false);
  const [
    visibleModalSuccessfulApproveKrookie,
    setVisibleModalSuccessfulApproveKrookie,
  ] = useState<boolean>(false);
  const [visibleModalRejectKrookie, setVisibleModalRejectKrookie] =
    useState<boolean>(false);
  const [visibleCoordinates, setVisibleCoordinates] = useState<boolean>(false);
  const [verified, setVerified] = useState<boolean>(false);
  const [selectedPlotNumber, setSelectedPlotNumber] = useState<string>("");

  useEffect(() => {
    if (!layers.length) return;

    dispatch(fetchKrookies());
  }, [layers, arcgisTokens]);

  const createRowMenuItems = (rowData: any): MenuItem[] => [
    {
      label: t("krookieJournal.viewKrookie"),
      onClick: async () => {
        try {
          await dispatch(
            fetchKrookieData({
              sessionId: rowData.krookieSessionId,
              status: rowData.status,
              krookieId: rowData.id,
            }),
          ).unwrap();
          setVisibleDocumentKrookie(true);
        } catch (e) {
          console.error(e);
        }
      },
    },
    {
      label: t("krookieJournal.viewCoordinates"),
      onClick: async () => {
        setSelectedPlotNumber(rowData.newPlotNum);
        await dispatch(
          fetchCoordinatesData({
            krookieId: rowData.id,
            PlotId: rowData.newPlotId,
          }),
        );
        setVisibleCoordinates(true);
      },
    },
  ];

  const closeModalApprove = async () => {
    setVisibleModalSuccessfulApproveKrookie(false);
  };

  const closeModalReject = async () => {
    setVisibleModalRejectKrookie(false);
  };

  const onClickViewChanges = () => {
    setVisibleDocumentKrookie(false);
    closeModalReject();
    dispatch(setResetKrookieData());
  };

  if (!fullData) return <></>;

  return (
    <>
      <TableContainer
        idField="id"
        tableName={t("krookieJournal.tableName")}
        tableIcon={krookieJournalIcon}
        dataSource={fullData}
        rowHeight={76}
        dateTemplate="MM/DD/YYYY hh:mm pm"
        debounceMs={1000}
        data={data}
        totalDataSize={totalDataSize}
        selected={selected}
        addSelected={(ids) => dispatch(addSelected(ids))}
        deleteSelected={(ids) => dispatch(deleteSelected(ids))}
        deleteAllSelected={() => dispatch(deleteAllSelected())}
        setData={(payload) => dispatch(setData(payload))}
        placeholderSearch={t("krookieJournal.searchPlaceholder")}
        substringFields={["newPlotNum", "status", "timeAndDrafter"]}
        footerContent={({
          selected,
          filters,
          currentPage,
          substringFields,
        }) => (
          <ExportButtons
            selected={selected}
            dataSource={fullData}
            filters={filters}
            currentPage={currentPage}
            substringFields={substringFields}
          />
        )}
        renderRowMenu={(rowData) => (
          <RowMenu items={createRowMenuItems(rowData)} />
        )}
        isLoading={isLoading}
      >
        <TableColumn
          dataField="newPlotNum"
          caption={t("krookieJournal.columns.plotNumber")}
          style={{ width: "112px", textAlign: "end" }}
        />
        <TableColumn
          dataField="status"
          caption={t("krookieJournal.columns.krookieStatus")}
          style={{ width: "132px", textAlign: "start" }}
        />
        <TableColumn
          dataField="cityAndAddress"
          caption={t("krookieJournal.columns.cityAddress")}
          style={{ width: "188px", textAlign: "start" }}
          renderCell={(value: string) => {
            const [city = "", address = ""] = value?.split("\n") ?? [];
            return (
              <div className={styles.textPairColumn}>
                <span className={styles.textSingleLine}>{city}</span>
                <span className={styles.textDoubleLine}>{address}</span>
              </div>
            );
          }}
        />
        <TableColumn
          dataField="plotStatus"
          caption={t("krookieJournal.columns.landStatus")}
          style={{ width: "112px", textAlign: "start" }}
          renderCell={(value) => value ? renderStatusTag(value) : null}
        />
        <TableColumn
          dataField="area"
          caption={t("krookieJournal.columns.plotArea")}
          style={{ width: "124px", textAlign: "end" }}
        />
        <TableColumn
          dataField="timeAndDrafter"
          caption={t("krookieJournal.columns.formationDateDrafter")}
          style={{ width: "160px" }}
          renderCell={(value: string) => {
            const [time = "", drafter = ""] = value?.split("\n") ?? [];
            return (
              <div className={styles.textPairColumn}>
                <span>{time}</span>
                <span className={styles.textDoubleLine}>{drafter}</span>
              </div>
            );
          }}
        />
        <TableColumn
          dataField="landUse"
          caption={t("krookieJournal.columns.landUse")}
          style={{ width: "132px" }}
        />
        <TableColumn
          dataField="-"
          caption={t("krookieJournal.columns.tenant")}
          style={{ width: "144px", marginInlineEnd: "12px" }}
        />
      </TableContainer>

      {visibleDocumentKrookie && convertedKrookieData && (
        <DocumentKrookie
          setVisibleModalApproveKrookie={
            setVisibleModalSuccessfulApproveKrookie
          }
          setVisibleModalRejectKrookie={setVisibleModalRejectKrookie}
          setVisibleDocumentKrookie={setVisibleDocumentKrookie}
          data={convertedKrookieData.krookies}
          status={Object.values(convertedKrookieData.krookies)[0]?.statusId}
          origin={DocumentOrigin.Journal}
          krookieFormationTypeId={
            Object.values(convertedKrookieData.krookies)[0]
              ?.krookieFormationTypeId
          }
        />
      )}

      {visibleModalSuccessfulApproveKrookie && convertedKrookieData && (
        <ModalSuccessfulApproveKrookie onClose={closeModalApprove} />
      )}

      {visibleModalRejectKrookie && convertedKrookieData && (
        <ModalRejectKrookie
          onClose={closeModalReject}
          onClickViewChanges={onClickViewChanges}
        />
      )}

      {visibleCoordinates && coordinatesData && (
        <Modal onClose={() => setVisibleCoordinates(false)}>
          <CoordinatesBlock
            setVisibleCoordinates={setVisibleCoordinates}
            initialData={coordinatesData}
            verified={verified}
            setVerified={setVerified}
            showButtons={{
              paste: false,
              clear: false,
              addContour: false,
              copy: true,
              getGeometry: false,
              delete: false,
            }}
            plotNumber={selectedPlotNumber}
            classes={{
              container: styles.coordinatesBlockContainer,
            }}
          />
        </Modal>
      )}
    </>
  );
};

export default KrookieJournal;
